import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { UploadSimple } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { api, formatApiErrorDetail } from "../../lib/api";

export function RepCsvUploadModal({ rep, apiBasePath, onClose, onSuccess }) {
  const [category, setCategory] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [gateBlockReason, setGateBlockReason] = useState(null);
  const [confirmingOverride, setConfirmingOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const submit = async (override = false) => {
    if (!file) { toast.error("Choose a CSV file to upload"); return; }
    if (override && !overrideReason.trim()) { toast.error("A reason is required to override a gate"); return; }
    setUploading(true);
    setProgress(0);
    setResult(null);
    setGateBlockReason(null);
    setConfirmingOverride(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const reasonParam = override ? `&override=true&override_reason=${encodeURIComponent(overrideReason.trim())}` : "";
      const { data } = await api.post(
        `${apiBasePath}/reps/${rep.id}/upload-leads?category=${category}${reasonParam}`,
        fd,
        { onUploadProgress: (evt) => setProgress(Math.round((evt.loaded / evt.total) * 100)) }
      );
      setResult(data);
      setOverrideReason("");
      toast.success(data.message);
      onSuccess?.();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      const lower = (detail || "").toLowerCase();
      if (lower.includes("replenishment gate") || lower.includes("lead upload threshold gate")) {
        setGateBlockReason(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setUploading(false);
    }
  };

  const combinedRemaining = (rep.trust_remaining ?? 0) + (rep.funding_remaining ?? 0);

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="rep-csv-upload-overlay">
      <div className="bg-igc-surface border border-igc-gold/30 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="rep-csv-upload-modal">
        <p className="font-serif text-xl text-igc-cream mb-1">Upload Leads</p>

        {!category ? (
          <div data-testid="rep-csv-upload-category-gate">
            <p className="text-igc-muted text-sm mb-5">Which category is this CSV for? {rep.display_name} won't see a single row until you pick one — this can't be skipped or defaulted.</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="rep-csv-upload-category-trust-button"
                type="button"
                onClick={() => setCategory("trust")}
                title="This CSV contains Trust & Asset Protection leads."
                className="border border-igc-gold/25 hover:border-igc-gold hover:bg-igc-gold/10 transition-colors py-6 text-center"
              >
                <p className="text-igc-cream text-sm font-medium">Trust</p>
                <p className="text-igc-muted text-xs mt-1">& Asset Protection</p>
              </button>
              <button
                data-testid="rep-csv-upload-category-funding-button"
                type="button"
                onClick={() => setCategory("funding")}
                title="This CSV contains Business Funding leads."
                className="border border-igc-gold/25 hover:border-igc-gold hover:bg-igc-gold/10 transition-colors py-6 text-center"
              >
                <p className="text-igc-cream text-sm font-medium">Funding</p>
                <p className="text-igc-muted text-xs mt-1">Business Funding</p>
              </button>
            </div>
          </div>
        ) : (
        <>
        <p className="text-igc-muted text-sm mb-4">Load a CSV of new leads directly into {rep.display_name}'s folder. Duplicate phone numbers are stripped automatically.</p>

        <div className="flex items-center justify-between mb-4" data-testid="rep-csv-upload-category-chosen">
          <span className="text-xs uppercase tracking-widest text-igc-muted">Category: <span className="text-igc-goldText">{category === "trust" ? "Trust & Asset Protection" : "Business Funding"}</span></span>
          <button data-testid="rep-csv-upload-category-change-button" type="button" onClick={() => setCategory(null)} title="Pick a different category" className="text-xs text-igc-muted hover:text-igc-goldText underline">
            Change
          </button>
        </div>

        <input data-testid="rep-csv-upload-file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} title="Choose the CSV file of leads to upload for this rep." className="text-igc-cream text-sm mb-4 w-full" />

        {uploading && (
          <div className="mb-4">
            <Progress value={progress} className="h-2" data-testid="rep-csv-upload-progress" />
            <p className="text-igc-muted text-xs mt-1">{progress}%</p>
          </div>
        )}

        {gateBlockReason && !confirmingOverride && (
          <div className="bg-igc-danger/10 border border-igc-danger/30 p-3 text-sm mb-4" data-testid="rep-csv-upload-gate-blocked">
            <p className="text-igc-danger">{gateBlockReason}</p>
            <Button
              data-testid="rep-csv-upload-override-button"
              type="button"
              disabled={uploading}
              onClick={() => setConfirmingOverride(true)}
              title="Bypasses the lead threshold gate and uploads anyway — logged to the audit trail."
              className="mt-2 h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black"
            >
              Override & Upload Anyway
            </Button>
          </div>
        )}

        {confirmingOverride && (
          <div className="bg-amber-950/40 border border-amber-600/40 p-3 text-sm mb-4" data-testid="rep-csv-upload-override-confirm">
            <p className="text-amber-500 font-medium mb-1">Confirm Gate Override</p>
            <p className="text-igc-cream text-xs">
              {rep.display_name} currently has <span className="text-amber-500 font-medium">{combinedRemaining} leads remaining</span> (trust + funding combined).
              Uploading anyway bypasses the gate for this rep — this action is logged to the audit trail.
            </p>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mt-3 mb-1 block">Reason for Override (required, max 200 chars)</label>
            <textarea
              data-testid="rep-csv-upload-override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value.slice(0, 200))}
              maxLength={200}
              rows={2}
              placeholder="e.g. New rep fast-track, manager approved catch-up upload..."
              title="Explain why you're bypassing the lead threshold gate for this rep. Required, logged for audit."
              className="w-full bg-igc-surfaceLight border border-igc-gold/20 text-igc-cream text-xs p-2 resize-none focus:outline-none focus:border-igc-gold/50"
            />
            <p className="text-igc-muted text-[10px] mt-1 text-right">{overrideReason.length}/200</p>
            <div className="flex gap-2 mt-2">
              <Button
                data-testid="rep-csv-upload-override-confirm-button"
                type="button"
                disabled={uploading || !overrideReason.trim()}
                onClick={() => submit(true)}
                title="Confirms and uploads this CSV, bypassing the lead threshold gate."
                className="flex-1 h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40"
              >
                Confirm & Upload Anyway
              </Button>
              <Button data-testid="rep-csv-upload-override-cancel-button" type="button" onClick={() => { setConfirmingOverride(false); setOverrideReason(""); }} variant="outline" title="Cancels the override and goes back." className="flex-1 h-8 text-xs border-igc-gold/30 text-igc-goldText">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-igc-success/10 border border-igc-success/30 p-3 text-sm mb-4" data-testid="rep-csv-upload-result">
            <p className="text-igc-success">{result.message}</p>
            {result.error_count > 0 && <p className="text-igc-muted text-xs mt-1">{result.error_count} row(s) skipped — see: {result.errors?.[0]}</p>}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button data-testid="rep-csv-upload-submit-button" onClick={() => submit(false)} disabled={uploading} title="Uploads the leads in this CSV to this rep's folder." className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black flex items-center justify-center gap-2">
            <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
          <Button data-testid="rep-csv-upload-close-button" onClick={onClose} variant="outline" title="Closes this window without uploading anything." className="border-igc-gold/30 text-igc-goldText">Close</Button>
        </div>
        </>
        )}

        {!category && (
          <div className="mt-5">
            <Button data-testid="rep-csv-upload-gate-close-button" onClick={onClose} variant="outline" title="Closes this window without uploading anything." className="w-full border-igc-gold/30 text-igc-goldText">Close</Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
